import { useCallback } from 'react';
import { Link as RouterLink } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { Box, Button, Divider, Grid, Link, Paper, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useRouter } from '@cvt/routing';
import { useDictionary } from '@cvt/hooks/useDictionary';
import { emailRegex } from '@cvt/helpers/validation';

import config from '@shared/config';
import { Logo } from '@shared/components/Logo';

import { useSignin } from '../hooks/useSignin';
import { LoggedOutActionsLayout } from '../components/LoggedOutActionsLayout';

export const Login = () => {
  const dictionary = useDictionary();

  const { control, setError, handleSubmit, formState: { isSubmitting, errors } } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const router = useRouter();

  const { signIn, signInWithSSO } = useSignin();

  const onSSOLogin = useCallback(async (provider: string) => {
    try {
      await signInWithSSO(provider);
    } catch (e: any) {
      // @ts-ignore
      setError('email', { type: 'error', message: dictionary.auth.firebase.errors[e.error] || e.message });
    }
  }, [signInWithSSO, setError, dictionary]);

  const onSubmit = useCallback (async ({ email, password }: { email: string; password: string }) => {

    try {
      await signIn(email, password);
    } catch (e: any) {
      if(['auth/invalid-email','auth/user-not-found', 'auth/wrong-password', 'auth/invalid-login-credentials'].includes(e.error)) {
        setError('email', { type: 'error', message: dictionary.auth.validations.emailOrPasswordWrong });
        setError('password', { type: 'error', message: dictionary.auth.validations.emailOrPasswordWrong });
      } else {
        setError('email', { type: 'error', message: dictionary.errors.somethingWentWrong });
      }
    }
  }, [signIn, setError, dictionary]);


  return (
    <LoggedOutActionsLayout>
      <Stack direction="row" alignItems="center" justifyContent="center" mb={3} position="relative">
        <Logo/>
      </Stack>
      <Paper component={Box} elevation={1} p={3}>
        {(config.featureFlags.appleSSO || config.featureFlags.googleSSO || config.featureFlags.microsoftSSO || config.featureFlags.phoneLogin) && (
          <>
            <Stack direction="column" justifyContent="center" spacing={2}>
              {config.featureFlags.googleSSO && (
                <Button
                  fullWidth
                  onClick={() => onSSOLogin('google')}
                  startIcon={<img src="/google.svg" alt="Google" width={18} height={18} />}
                  size="large"
                  variant="contained"
                  color="primary"
                >
                  {dictionary.auth.login.buttonLoginWithGoogle}
                </Button>
              )}
            </Stack>
            <Box my={3}>
              <Divider
                orientation="horizontal"
                flexItem
                sx={{
                  color: 'grey.500',
                  fontSize: 12,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                }}
              >
                {dictionary.auth.divider}
              </Divider>
            </Box>
          </>
        )}
        <div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container direction="column">
              <Grid size={12}>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: dictionary.forms.validations.required,
                    pattern: {
                      value: emailRegex,
                      message: dictionary.forms.validations.invalidEmail,
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      label={dictionary.forms.fieldEmail}
                      name="email"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Grid>
              <br/>
              <Grid size={12}>
                <Controller
                  name="password"
                  control={control}
                  rules={{ required: dictionary.forms.validations.required }}
                  render={({ field }) => (
                    <Stack>
                      <TextField
                        {...field}
                        fullWidth
                        variant="outlined"
                        label={dictionary.forms.fieldPassword}
                        name="password"
                        type="password"
                        error={!!errors.password}
                        helperText={errors.password?.message}
                      />
                      <Box textAlign="right" mt={0.5}>
                        <Typography variant="body2">
                          <Link
                            component={RouterLink}
                            to={router.auth.resetPassword('forgotten').path}
                            underline="hover"
                          >
                            {dictionary.auth.login.buttonForgotPassword}
                          </Link>
                        </Typography>
                      </Box>
                    </Stack>
                  )}
                />
              </Grid>
              <Grid size={12} pt={2}>
                <LoadingButton
                  fullWidth
                  type="submit"
                  loading={isSubmitting}
                  size="large"
                  variant="contained"
                  color="primary"

                >
                  {dictionary.auth.login.buttonLogin}
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        </div>
      </Paper>
      <Box mt={3} textAlign="center">
        <Stack direction="row" justifyContent="center" alignItems="center" mt={1}>
          <Typography variant="body2" color="secondary.dark">
            {dictionary.auth.login.dontHaveAccount}
            {' '}
            <Link
              component={RouterLink}
              to={router.auth.signup().path}
              underline="hover"
              color="secondary"
            >
              {dictionary.auth.signUp.buttonSignup}
            </Link>
          </Typography>
        </Stack>
      </Box>
    </LoggedOutActionsLayout>
  );
};
